import type { Anomaly } from '../models/anomaly.types'

import { DATA_COLLATION_LOCALE, strIncludes } from '@/shared/lib/string'

export interface AnomalyFilters {
  metricField?: string | null
  dimensionKey?: string | null
  query?: string
}

export interface FilterAnomaliesContext {
  titleOf?: (anomaly: Anomaly) => string
  metricLabel?: (metricField: string) => string
}

export interface AnomalyDimension {
  value: string
  segmentName: string
  subSegmentRaw: string
  subSegmentName: string
}

export function anomalyDimensionKey(anomaly: Anomaly): string {
  return `${anomaly.segmentName}|${anomaly.subSegmentRaw}`
}

function matchesQuery(anomaly: Anomaly, query: string, context: FilterAnomaliesContext): boolean {
  if (!query.trim()) return true
  const metricText = anomaly.relatedMetricFields
    .flatMap((field) => [field, context.metricLabel?.(field) ?? ''])
    .join(' ')
  const title = context.titleOf?.(anomaly) ?? anomaly.narrative.titleParams.metric
  return [
    title,
    metricText,
    anomaly.segmentName,
    anomaly.subSegmentName,
    anomaly.subSegmentRaw,
  ].some((value) => strIncludes(value, query))
}

/** Apply the legacy client filters without mutating or re-projecting anomaly records. */
export function filterAnomalies(
  anomalies: readonly Anomaly[],
  filters: AnomalyFilters,
  context: FilterAnomaliesContext = {},
): Anomaly[] {
  return anomalies.filter((anomaly) => {
    if (filters.metricField && !anomaly.relatedMetricFields.includes(filters.metricField)) {
      return false
    }
    if (filters.dimensionKey && anomalyDimensionKey(anomaly) !== filters.dimensionKey) {
      return false
    }
    return matchesQuery(anomaly, filters.query ?? '', context)
  })
}

export function uniqueMetrics(
  anomalies: readonly Anomaly[],
  metricLabel: (metricField: string) => string = (field) => field,
  locale: string = DATA_COLLATION_LOCALE,
): string[] {
  const fields = new Set(anomalies.flatMap((anomaly) => anomaly.relatedMetricFields))
  return [...fields].sort((left, right) =>
    metricLabel(left).localeCompare(metricLabel(right), locale, { sensitivity: 'base' }),
  )
}

export function uniqueDimensions(
  anomalies: readonly Anomaly[],
  locale: string = DATA_COLLATION_LOCALE,
): AnomalyDimension[] {
  const dimensions = new Map<string, AnomalyDimension>()
  for (const anomaly of anomalies) {
    if (!anomaly.subSegmentRaw) continue
    const value = anomalyDimensionKey(anomaly)
    dimensions.set(value, {
      value,
      segmentName: anomaly.segmentName,
      subSegmentRaw: anomaly.subSegmentRaw,
      subSegmentName: anomaly.subSegmentName,
    })
  }
  return [...dimensions.values()].sort((left, right) =>
    `${left.segmentName}:${left.subSegmentName}`.localeCompare(
      `${right.segmentName}:${right.subSegmentName}`,
      locale,
      { sensitivity: 'base' },
    ),
  )
}
